import type { IComponentController, IScope } from "angular";
import { finalize } from "rxjs/operators";

import type { IAccountRepository, IProfileService } from "App/Modules/Auth";

import type { IFormContainerResolve } from "App/Modules/Services";
import { PatchDelta } from "App/Modules/Services";

import { cloneDeep } from "lodash";

import "angular-translate";

import type { IProfile, IResponseWithLoading } from "App/Modules/Models";
import { TwoFactorAuthMode } from "App/Modules/Models";
import { extendWithLoadingAndFail } from "../../../../../Services/ObservableExtensions";

export default class PasswordEditComponentController implements IComponentController {
    public static $inject = ["$scope", "$translate", "mmProfileService", "mmAccountRepository"];

    public original: IProfile;

    public edited: IProfile;

    public displayValidationError = false;

    public resolve: IFormContainerResolve<IProfile, void>;

    public editResponse: IResponseWithLoading<IProfile>;

    constructor(
        private $scope: IScope,
        private $translate: angular.translate.ITranslateService,
        private profileService: IProfileService,
        private accountRepository: IAccountRepository,
    ) {}

    public $onInit(): void {
        this.original = this.resolve.formData;
        this.edited = cloneDeep(this.original);
        this.edited.enableTwoFactorAuth = !!this.original.twoFactorAuthentication;
        this.$scope.$watch("$ctrl.profilePatch.currentPassword", (currentPassword) => {
            if (currentPassword !== undefined) {
                this.displayValidationError = false;
            }
        });
    }

    public customValidate() {
        if (this.edited.currentPassword === undefined) {
            this.displayValidationError = true;
        }
    }

    public cancel() {
        this.resolve.options.onCancel();
    }

    public save() {
        const delta = PatchDelta.difference(this.original, this.edited);
        if (this.edited.enableTwoFactorAuth !== !!this.original.twoFactorAuthentication) {
            if (this.edited.enableTwoFactorAuth) {
                delta.twoFactorAuthentication = TwoFactorAuthMode.GoogleAuthenticator;
            } else {
                delta.twoFactorAuthentication = null;
            }
        }

        extendWithLoadingAndFail(this.profileService.patch(delta))
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe(
                (editResponse: IResponseWithLoading<IProfile>) => {
                    this.editResponse = editResponse;
                    if (!editResponse.isLoading && editResponse.data) {
                        if (
                            this.edited.enableTwoFactorAuth &&
                            this.edited.enableTwoFactorAuth !== !!this.original.twoFactorAuthentication
                        ) {
                            this.accountRepository.twoFactorAuthentication(this.original.email).subscribe();
                        }
                        this.resolve.options.onOk(editResponse.data);
                    }
                },
                (errorResponse) => {
                    this.editResponse = errorResponse;
                },
            );
    }
}
